// ============================================================
// SALT — Report Content Button
// Single "Report" link that opens a modal with content type
// selection, reason picker, and optional details.
//
// Usage (User Profile):
//   <ReportButton
//     targetId={userId}
//     reportableContent={[
//       { type: 'user_avatar', label: 'Profile Photo' },
//       { type: 'user_bio', label: 'Bio' },
//     ]}
//   />
//
// Usage (Team Profile):
//   <ReportButton
//     targetId={teamId}
//     reportableContent={[
//       { type: 'team_logo', label: 'Team Logo' },
//       { type: 'team_name', label: 'Team Name' },
//       { type: 'team_description', label: 'Team Description' },
//     ]}
//   />
// ============================================================

import React, { useState } from 'react';
import Modal from './Modal';
import axiosClient from '../../api/axiosClient';
import { useAuthContext } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';

// ============================================================
// Types
// ============================================================

type ContentType =
  | 'user_avatar'
  | 'team_logo'
  | 'user_bio'
  | 'team_description'
  | 'ign'
  | 'team_name';

type ReportReason =
  | 'inappropriate'
  | 'offensive'
  | 'harassment'
  | 'spam'
  | 'other';

interface ReportableItem {
  type: ContentType;
  label: string;
}

interface ReportButtonProps {
  targetId: number;
  reportableContent: ReportableItem[];
}

// ============================================================
// Constants
// ============================================================

const REASONS: { value: ReportReason; label: string }[] = [
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'offensive', label: 'Offensive or hateful' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'spam', label: 'Spam or misleading' },
  { value: 'other', label: 'Other' },
];

const DETAILS_MAX = 500;

// ============================================================
// Component
// ============================================================

const ReportButton: React.FC<ReportButtonProps> = ({
  targetId,
  reportableContent,
}) => {
  const { user } = useAuthContext();
  const toast = useToast();

  // Modal state
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'content' | 'reason'>('content');
  const [selectedContent, setSelectedContent] = useState<ContentType | ''>('');
  const [selectedReason, setSelectedReason] = useState<ReportReason | ''>('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Don't render if not logged in or no reportable content
  if (!user || reportableContent.length === 0) return null;

  const resetAndClose = () => {
    setIsOpen(false);
    setStep('content');
    setSelectedContent('');
    setSelectedReason('');
    setDetails('');
  };

  const handleOpen = () => {
    if (submitted) {
      toast.info('Already Reported', 'You have already submitted a report for this content.');
      return;
    }
    // If only one content type, skip the content selection step
    if (reportableContent.length === 1) {
      setSelectedContent(reportableContent[0].type);
      setStep('reason');
    } else {
      setStep('content');
    }
    setIsOpen(true);
  };

  const handleContentNext = () => {
    if (!selectedContent) return;
    setStep('reason');
  };

  const handleSubmit = async () => {
    if (!selectedContent || !selectedReason) return;

    try {
      setSubmitting(true);
      await axiosClient.post('/reports', {
        contentType: selectedContent,
        targetId,
        reason: selectedReason,
        details: details.trim() || undefined,
      });

      setSubmitted(true);
      resetAndClose();
      toast.success('Report Submitted', 'Thank you. Our team will review this content.');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Unable to submit report. Please try again.';

      // Handle duplicate report
      if (err?.response?.status === 409) {
        setSubmitted(true);
        resetAndClose();
        toast.info('Already Reported', 'You have already reported this content.');
      } else {
        toast.error('Report Failed', message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLabel = reportableContent.find(
    (r) => r.type === selectedContent
  )?.label;

  return (
    <>
      <button
        type="button"
        className="sl-report-link"
        onClick={handleOpen}
        disabled={submitted}
        title={submitted ? 'Report already submitted' : 'Report this content'}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
          <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
        {submitted ? 'Reported' : 'Report'}
      </button>

      <Modal
        isOpen={isOpen}
        title="Report Content"
        onClose={resetAndClose}
        onCancel={step === 'reason' && reportableContent.length > 1
          ? () => setStep('content')
          : resetAndClose
        }
        cancelLabel={step === 'reason' && reportableContent.length > 1 ? 'Back' : 'Cancel'}
        onConfirm={step === 'content' ? handleContentNext : handleSubmit}
        confirmLabel={
          step === 'content'
            ? 'Next'
            : submitting
              ? 'Submitting...'
              : 'Submit Report'
        }
        disableConfirm={
          step === 'content'
            ? !selectedContent
            : !selectedReason || submitting
        }
        disableCancel={submitting}
      >
        {step === 'content' ? (
          <div className="sl-report-modal-body">
            <p className="sl-report-modal-label">
              What would you like to report?
            </p>
            <div className="sl-report-options">
              {reportableContent.map((item) => (
                <label key={item.type} className="sl-report-option">
                  <input
                    type="radio"
                    name="reportContent"
                    value={item.type}
                    checked={selectedContent === item.type}
                    onChange={() => setSelectedContent(item.type)}
                  />
                  <span className="sl-report-option-label">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        ) : (
          <div className="sl-report-modal-body">
            {reportableContent.length > 1 && (
              <p className="sl-report-modal-context">
                Reporting: <strong>{selectedLabel}</strong>
              </p>
            )}
            <p className="sl-report-modal-label">
              Why are you reporting this?
            </p>
            <div className="sl-report-options">
              {REASONS.map((r) => (
                <label key={r.value} className="sl-report-option">
                  <input
                    type="radio"
                    name="reportReason"
                    value={r.value}
                    checked={selectedReason === r.value}
                    onChange={() => setSelectedReason(r.value)}
                  />
                  <span className="sl-report-option-label">{r.label}</span>
                </label>
              ))}
            </div>
            <div className="sl-report-details-wrap">
              <textarea
                className="sl-report-details"
                placeholder="Additional details (optional)"
                value={details}
                onChange={(e) => setDetails(e.target.value.slice(0, DETAILS_MAX))}
                rows={3}
                maxLength={DETAILS_MAX}
              />
              <span className="sl-report-details-count">
                {details.length}/{DETAILS_MAX}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ReportButton;